/**
 * Redeeming an invitation link. The token is checked server-side (POST /api/invites/accept),
 * which also creates the membership - the browser has no rights to do that itself.
 *
 * Signed out, the link first says what it is for (GET /api/invites/preview/:token): the business,
 * who invited and as what. Signing up or in from there carries the invited address along and
 * comes back here, where the invitation is accepted.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAcceptInvite } from "@/hooks/use-workspaces";
import { previewInvite } from "@/lib/api/workspaces";
import { useAuth } from "@/lib/session/auth-context";
import { useWorkspace } from "@/lib/session/workspace-context";

export function InvitePage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const { isAuthenticated, loading } = useAuth();
  const { select } = useWorkspace();
  const navigate = useNavigate();
  const acceptInvite = useAcceptInvite();
  const [error, setError] = useState("");
  const attempted = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !token || attempted.current) return;
    attempted.current = true;
    acceptInvite
      .mutateAsync(token)
      .then((result) => {
        select(result.workspace);
        toast.success(
          t("invite.joined", { workspace: result.workspaceName, role: t(`roles.${result.role}`) }),
        );
        navigate("/dashboard", { replace: true });
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : t("invite.failed"));
      });
  }, [isAuthenticated, token, acceptInvite, select, navigate, t]);

  if (loading) return null;
  if (!isAuthenticated) return <InvitePreviewCard token={token ?? ""} />;

  return (
    <div className="mx-auto max-w-md">
      <Card>
        <CardHeader>
          <CardTitle>{t("invite.title")}</CardTitle>
          <CardDescription>{error ? t("invite.problem") : t("invite.working")}</CardDescription>
        </CardHeader>
        {error && (
          <CardContent className="space-y-4">
            <p role="alert" className="text-sm text-destructive" data-testid="invite-error">
              {error}
            </p>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              {t("invite.toDashboard")}
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

/** Signed out: what the invitation is, and the way in with the invited address filled in. */
function InvitePreviewCard({ token }: { token: string }) {
  const { t } = useTranslation();
  const preview = useQuery({
    queryKey: ["invite-preview", token],
    queryFn: () => previewInvite(token),
    retry: false,
  });
  const from = { from: `/invite/${token}` };

  if (preview.isPending) return <Skeleton className="mx-auto h-48 max-w-md" aria-busy="true" />;
  const invite = preview.data;
  const refusal = !invite
    ? t("settings.inviteRefused.invalid")
    : invite.state !== "open"
      ? t(`settings.inviteRefused.${invite.state}`)
      : "";
  const login = (tab: "register" | "login") =>
    `/login?tab=${tab}&email=${encodeURIComponent(invite?.email ?? "")}`;

  return (
    <div className="mx-auto max-w-md">
      <Card data-testid="invite-preview">
        <CardHeader>
          <CardTitle>
            {invite?.workspaceName
              ? t("invite.previewTitle", { workspace: invite.workspaceName })
              : t("invite.title")}
          </CardTitle>
          {invite && (
            <CardDescription>
              {t("invite.previewText", {
                inviter: invite.inviterName || invite.workspaceName,
                email: invite.email,
                role: t(`roles.${invite.role}`),
              })}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {refusal ? (
            <p role="alert" className="text-sm text-destructive" data-testid="invite-error">
              {refusal}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{t("invite.previewNext")}</p>
              <div className="flex flex-wrap gap-2">
                <Button asChild data-testid="invite-register">
                  <Link to={login("register")} state={from}>
                    {t("invite.register")}
                  </Link>
                </Button>
                <Button asChild variant="outline" data-testid="invite-sign-in">
                  <Link to={login("login")} state={from}>
                    {t("invite.signIn")}
                  </Link>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
